import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useStylesheets } from '@/hooks/use-stylesheets';
import { type YepCodeJsonSchemaFormProps } from '@/components/json-schema-form/yepcode-json-schema-form';
import type { EmbedFormOptions } from '@/types';

export interface YepCodeFormPropsWithBrandingProps
  extends YepCodeJsonSchemaFormProps {
  embedFormOptions: EmbedFormOptions;
}
export const withBranding = (
  YepCodeJsonSchemaForm: React.ComponentType<YepCodeJsonSchemaFormProps>
): React.ComponentType<YepCodeFormPropsWithBrandingProps> => {
  const WithBranding = ({
    className,
    ...props
  }: YepCodeFormPropsWithBrandingProps) => {
    const { embedFormOptions = {} as any } = props;
    const { stylesheet, theme, themeStylesheet, withBranding } =
      embedFormOptions;

    const stylesheets = useMemo(() => {
      if (stylesheet) {
        return [stylesheet];
      }
      const stylesheets: string[] = ['https://yepcode.io/sdk/styles.css'];
      if (themeStylesheet) {
        stylesheets.push(themeStylesheet);
      } else if (theme && theme !== 'none') {
        stylesheets.push(`https://yepcode.io/sdk/styles-theme-${theme}.css`);
      }

      return stylesheets;
    }, [theme, themeStylesheet]);

    useStylesheets({ stylesheets });

    const setBrandingStyles = (brandingElement?: any): void => {
      if (brandingElement) {
        brandingElement.style.setProperty('display', 'flex', 'important');
        brandingElement.style.setProperty('opacity', 'initial', 'important');
        brandingElement.style.setProperty('visibility', 'visible', 'important');
      }
    };

    return (
      <>
        <YepCodeJsonSchemaForm
          {...props}
          className={classNames([
            className,
            {
              [`theme-${theme}`]: theme,
            },
          ])}
        />
        {withBranding && (
          <div className="powered-by-yepcode" ref={setBrandingStyles}>
            <a
              href="https://yepcode.io/"
              target="_blank"
              rel="noreferrer noopener"
              ref={setBrandingStyles}
            >
              <img
                alt="Powered by YepCode"
                src="https://yepcode.io/sdk/powered-by-yepcode.svg"
                ref={setBrandingStyles}
              />
            </a>
          </div>
        )}
      </>
    );
  };
  return WithBranding;
};
